import { useQuery } from '@apollo/client';
import { RHFAutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/RHFAutoCompleteVirtualize';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form';
import { useSnackbar } from 'notistack';
import { useMemo } from 'react';
import { type Path, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { useAuth } from '../../../../components/AuthContext/context';
import {
  GetProvidersDocument,
  type GetProvidersQuery,
} from '../../../../graphql/query/audiences/GetProviders.generated';
import { isAdmin } from '../../../../permissions/validators';
import { exportType, MatchingOperator } from '../constants';
import { AudienceFields, type AudienceForm } from '../types';

type ProviderOptionsProps = {
  isEdit?: boolean;
};

export const ProviderOptions = ({ isEdit }: ProviderOptionsProps) => {
  const { t } = useTranslation();
  const { session } = useAuth();
  const {
    setValue,
    formState: { isSubmitting },
    control,
  } = useFormContext<AudienceForm>();
  const observableAccountId = useWatch({ name: AudienceFields.OrganizationId, control });
  const accountIdVariables = isAdmin(session) ? { accountId: observableAccountId || '' } : {};
  const observableExportType = useWatch({ name: AudienceFields.ExportType, control });
  const { enqueueSnackbar } = useSnackbar();
  const {
    data: providersData,
    loading: loadingProviders,
    error,
  } = useQuery<GetProvidersQuery>(GetProvidersDocument, {
    variables: { ...accountIdVariables },
  });

  const filteredProviders = useMemo(() => {
    if (!providersData?.provider || !observableExportType) {
      return [];
    }

    return providersData.provider.filter(provider =>
      (provider.allowed_export_types || []).includes(observableExportType)
    );
  }, [providersData, observableExportType]);

  const directProviderOptions = useMemo(() => filteredProviders.map(({ id }) => id), [filteredProviders]);

  const getProviderName = (providerId: string) => {
    const provider = providersData?.provider.find(p => p.id === providerId);

    return provider?.name || '';
  };

  if (error) {
    enqueueSnackbar(t('Error loading providers'), { variant: 'error' });
    return null;
  }

  if (!observableExportType || observableExportType === exportType.OA_MATCH) {
    return null;
  }

  return (
    <FormSection title="Provider" rightColumnSize={RIGHT_COLUMN_WIDTH} divider>
      <RHFAutoCompleteVirtualize
        name={AudienceFields.DirectAudienceProvider satisfies Path<AudienceForm>}
        options={directProviderOptions}
        getOptionLabel={getProviderName}
        textFieldProps={{
          placeholder: t('[ Choose Segments Direct Provider ]'),
          label: t('Direct Provider'),
          margin: 'dense',
        }}
        onChange={(_, value) => {
          setValue(AudienceFields.DirectAudienceProvider, value);
          setValue(AudienceFields.ExportTargets, {
            cookie: false,
            ctv: false,
            device: false,
            id5: false,
            idl: false,
            ip: false,
          });
          setValue(AudienceFields.NormalizedDefinition, {
            excludes: {
              groups: [{ uuid: uuid(), operator: MatchingOperator.INTERSECTION }],
              operator: MatchingOperator.INTERSECTION,
            },
            includes: {
              groups: [{ uuid: uuid(), operator: MatchingOperator.INTERSECTION }],
              operator: MatchingOperator.INTERSECTION,
            },
          });
        }}
        disableClearable
        textFieldReadOnly
        loading={loadingProviders}
        data-test="direct-provider"
        disabled={isEdit || isSubmitting}
      />
    </FormSection>
  );
};
